import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, catchError, map, of } from "rxjs";

import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import {
  extractDemandeForRoute,
  isSameRoutePath,
  resolvePrestataireDemandRouteResult,
} from "../helpers/prestataire-demand-route";
import { FormateService } from "./formate.service";

@Injectable({
  providedIn: "root",
})
export class ProviderService {
  url = `${environment.apiUrl}`;
  authUrl = `${environment.authUrl}`;
  private demandData: any;
  private rdvDate: any;
  private demandDataSubject: Subject<any> = new Subject<any>();
  private rdvDateSubject = new BehaviorSubject<string>("");
  rdvDateSubject$ = this.rdvDateSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private formateService: FormateService
  ) { }

  storeDemandData(data: any) {
    this.demandData = data;
    this.demandDataSubject.next(this.demandData);
  }

  /** Extrait l'id métier d'une demande depuis le payload Pusher / store. */
  private resolveDemandeIdFromStorePayload(payload: any): number | string | null {
    if (payload == null) {
      return null;
    }
    const nested = payload.demande;
    if (nested != null && typeof nested === "object" && nested.id != null) {
      return nested.id;
    }
    if (typeof nested === "number" || typeof nested === "string") {
      return nested;
    }
    const any = payload as Record<string, unknown>;
    if (any["demande_id"] != null) {
      return any["demande_id"] as number | string;
    }
    if (any["demand_id"] != null) {
      return any["demand_id"] as number | string;
    }
    if (any["id_demande"] != null) {
      return any["id_demande"] as number | string;
    }
    const topId = payload.id;
    if (topId != null && typeof topId === "object" && topId.id != null) {
      return topId.id;
    }
    if (typeof topId === "number" || typeof topId === "string") {
      return topId;
    }
    return null;
  }

  private resolveTimeWaitingFromStorePayload(payload: any): any {
    if (payload == null || typeof payload !== "object") {
      return null;
    }

    return (
      payload.timeWaiting ??
      payload.time_remaining ??
      payload.time_waiting ??
      payload.data?.timeWaiting ??
      payload.data?.time_remaining ??
      null
    );
  }

  /** Champs wrapper Pusher à conserver si absents de la réponse API. */
  private preserveWrapperFieldsFromSources(
    merged: Record<string, unknown>,
    ...sources: Array<Record<string, unknown> | null | undefined>
  ): Record<string, unknown> {
    const wrapperKeys = [
      "clientLat",
      "clientLng",
      "timeWaiting",
      "time_remaining",
      "time_waiting",
    ] as const;

    for (const key of wrapperKeys) {
      if (merged[key] != null && merged[key] !== "") {
        continue;
      }
      for (const source of sources) {
        const value = source?.[key];
        if (value != null && value !== "") {
          merged[key] = value;
          break;
        }
      }
    }

    return merged;
  }

  storeAndDemandData(
    data: any,
    options?: { forceRefresh?: boolean }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (data == null) {
        resolve(null);
        return;
      }

      const incomingId = this.resolveDemandeIdFromStorePayload(data);
      const memoryId = this.resolveDemandeIdFromStorePayload(this.demandData);
      const forceRefresh = options?.forceRefresh === true;
      const shouldFetchFromApi =
        !!incomingId &&
        (forceRefresh || !this.demandData || incomingId !== memoryId);

      if (shouldFetchFromApi) {
        this.getDemandeById(incomingId).subscribe({
          next: (res: any) => {
            const demande = res.body;
            const formattedDemandData =
              this.formateService.getDemandeFormat(demande);
            const mergedDemandData = this.preserveWrapperFieldsFromSources(
              {
                ...this.demandData,
                ...data,
                demande: formattedDemandData,
              },
              data,
              this.demandData
            );
            this.demandData = mergedDemandData;
            this.storeDemandData(this.demandData);
            resolve(this.demandData);
          },
          error: (error) => {

            reject(error);
          },
        });
        return;
      }

      if (this.demandData) {
        const incomingDemand =
          data?.demande && typeof data.demande === "object"
            ? this.formateService.getDemandeFormat(data.demande)
            : this.demandData.demande ?? this.demandData;
        const incomingTimeWaiting =
          this.resolveTimeWaitingFromStorePayload(data) ??
          this.resolveTimeWaitingFromStorePayload(this.demandData);
        const mergedDemandData = {
          ...this.demandData,
          ...data,
          demande: incomingDemand,
        };

        if (incomingTimeWaiting != null && incomingTimeWaiting !== "") {
          mergedDemandData.timeWaiting = incomingTimeWaiting;
        }

        this.demandData = mergedDemandData;
        this.storeDemandData(this.demandData);

        resolve(this.demandData);
        return;
      }

      const current_demand = localStorage.getItem("current_demand");
      if (current_demand) {
        try {
          resolve(JSON.parse(current_demand));
        } catch {
          resolve(null);
        }
        return;
      }
      resolve(data || null);
    });
  }
  getDemandeById(id: any) {
    return this.http.get(`${this.authUrl}/frontoffice/demandes/${id}`, {
      params: {},
      observe: "response",
    });
  }
  setRdvDate(date: string) {
    this.rdvDateSubject.next(date);
  }

  getDemandData(): Observable<any> {
    return this.demandDataSubject.asObservable();
  }
  get currentDemande() {
    return this.demandData;
  }

  /*==============================
session Domains
================================*/
  startSession() {
    return this.http.post(`${this.authUrl}/frontoffice/start-day-prestataire`, {
      observe: "response",
    });
  }

  endSession() {
    return this.http.post(`${this.authUrl}/backoffice/end-day/user`, {
      observe: "response",
    });
  }
  /*==============================
accept demande
================================*/
  acceptDemande(payload: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/accept-demande`,
      payload,
      { observe: "response" }
    );
  }
  /*==============================
refuse demande
================================*/
  refuseDemande(demandId: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/verpass-demande/${demandId}`,
      { observe: "response" }
    );
  }
  timeoutDemande(demandId: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/timeout-demande/${demandId}`,
      { observe: "response" }
    );
  }
  /*==============================
meeting services
================================*/
  getMeetings(payload: any) {
    return this.http.post(`${this.authUrl}/frontoffice/getmeetings`, payload, {
      observe: "response",
    });
  }
  addMeetings(payload: any) {
    return this.http.post(`${this.authUrl}/frontoffice/meetings`, payload, {
      observe: "response",
    });
  }
  /*==============================
devis creation
================================*/
  generateDevis(payload: any) {
    return this.http.post(`${this.authUrl}/frontoffice/deivs`, payload, {
      observe: "response",
    });
  }
  /*==============================
devis pdf
================================*/
  generatePdf(id: number) {
    return this.http.get(`${this.authUrl}/frontoffice/pdf/devi/${id}`, {
      observe: "response",
    });
  }

  downloadDevisPdf(id: number) {
    return this.http.get(`${this.authUrl}/frontoffice/devis/${id}/download`, {
      responseType: "blob",
      observe: "response",
    });
  }
  /*==============================
devis pdf
================================*/
  getDevisInfo(id: number) {
    return this.http.get(`${this.authUrl}/frontoffice/deivs/${id}`, {
      observe: "response",
    });
  }

  /*==============================
get assigned Demand
================================*/
  getAssignedDemand() {
    return this.http.get(`${this.authUrl}/frontoffice/demandes-assign`, {
      observe: "response",
    });
  }
  /*==============================
get pre assigned Demand
================================*/
  getPrestatairePreAssignedDemands() {
    return this.http.get(
      `${this.authUrl}/frontoffice/prestataire-with-time-remaining`,
      {
        observe: "response",
      }
    );
  }
  /*==============================
signaler problem
================================*/
  signaleProblem(data: any) {
    return this.http.post(`${this.authUrl}/frontoffice/report-problem`, data, {
      observe: "response",
    });
  }
  destinationAchieved(data: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/destination-attent`,
      data,
      {
        observe: "response",
      }
    );
  }
  storeDemandImages(id: any, data: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/demande-image-preuve/${id}`,
      data,
      {
        observe: "response",
      }
    );
  }

  cancleDemand(data: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/demandes/update-statut/${data.id}`,
      data,
      {
        observe: "response",
      }
    );
  }
  notifyClient(id: any, data: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/apply-provider-devi/${id}`,
      data,
      {
        observe: "response",
      }
    );
  }
  cancelDemande(data: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/canceled-demande-prestataire`,
      data,
      {
        observe: "response",
      }
    );
  }
  getStats() {
    return this.http.get(
      `${this.authUrl}/frontoffice/prestataire/demand-status`,
      {
        params: {},
        observe: "response",
      }
    );
  }
  getCurrentDemands(): Observable<any> {
    return this.getAssignedDemand().pipe(
      map((res: any) => {
        if (res.body) {
          const data = res.body;
          const success = data.success;
          const demand = success ? data.demande : null;
          const demandExist = success && demand;
          if (demandExist) {
            this.demandData = this.formateService.getDemandeFormat(demand);
            this.demandData = {
              ...data,
              demande: this.demandData,
            };
            this.storeDemandData(this.demandData);

            localStorage.setItem("current_demand", JSON.stringify(this.demandData));
            return this.demandData;
          } else {
            localStorage.removeItem("current_demand");
            return null;
          }
        } else {
          localStorage.removeItem("current_demand");
          return null;
        }
      }),
      catchError((error) => {
        console.error("error -> ", error);
        return of(null);
      })
    );
  }
  prestataireDemands() {
    this.getAssignedDemand().subscribe((res: any) => {
      if (res.body) {
        const data = res.body;
        const success = data.success;
        const demand = success ? data.demande : null;
        const demandExist = success && demand;
        if (demandExist) {
          let demande = this.formateService.getDemandeFormat(demand);
          demande = {
            ...data,
            demande: demande,
          };
          this.storeDemandData(demande);

          localStorage.setItem("current_demand", JSON.stringify(demande));
          const routeResult = resolvePrestataireDemandRouteResult(
            extractDemandeForRoute(demande)
          );
          if (routeResult.action === "navigate") {
            if (!isSameRoutePath(this.router.url, routeResult.path)) {
              void this.router.navigate([routeResult.path]);
            }
          } else if (routeResult.showDevisModal) {
            const demandesPath = "/prestataire/demandes";
            if (!isSameRoutePath(this.router.url, demandesPath)) {
              void this.router.navigate([demandesPath]);
            }
          }
        } else {
          const currentRoute = this.router.url;
          if (currentRoute != "prestataire/track-demands") {
            this.storeDemandData(null);
            localStorage.removeItem("current_demand");
            setTimeout(() => {
              this.router.navigate([`prestataire/track-demands`]);
            }, 50);
          }
        }
      }
    });
  }

  getDocs(id: number) {
    return this.http.get(`${this.authUrl}/frontoffice/documents/${id}`, {
      params: {},
      observe: "response",
    });
  }

  getPrestatairesDocs() {
    return this.http.get(`${this.authUrl}/frontoffice/documents/prestataires`, {
      params: {},
      observe: "response",
    });
  }
  updateProfilePart3(form: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/change-document-profile`,
      form,
      { observe: "response" }
    );
  }
  updateProfilePhotoDocument(form: any) {
    return this.http.post(
      `${this.authUrl}/frontoffice/change-profile-photo-document`,
      form,
      { observe: "response" }
    );
  }
  updateProfileAddDocuments(form: any) {
    return this.http.post(`${this.authUrl}/frontoffice/documents`, form, {
      observe: "response",
    });
  }
}
